import { createApiClient } from '@/shared/api/api-client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type {
  AuthProviders,
  AuthTokens,
  CurrentUser,
  LoginInput,
  RegisterInput,
} from '../models/auth.types';

const authClient = createApiClient();

export function getAuthProviders(): Promise<AuthProviders> {
  return authClient.get<AuthProviders>(ENDPOINTS.auth.providers, {
    skipAuth: true,
    skipRefresh: true,
  });
}

export function login(input: LoginInput): Promise<AuthTokens> {
  return authClient.post<AuthTokens>(ENDPOINTS.auth.login, input, {
    skipAuth: true,
    skipRefresh: true,
  });
}

export function register(input: RegisterInput): Promise<AuthTokens> {
  return authClient.post<AuthTokens>(ENDPOINTS.auth.register, input, {
    skipAuth: true,
    skipRefresh: true,
  });
}

export function refresh(refreshToken: string): Promise<AuthTokens> {
  return authClient.post<AuthTokens>(
    ENDPOINTS.auth.refresh,
    { refreshToken },
    { skipAuth: true, skipRefresh: true },
  );
}

export function me(): Promise<CurrentUser> {
  return authClient.get<CurrentUser>(ENDPOINTS.auth.me);
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  locale?: string;
}

/** Self-service profile update — returns the refreshed `me` shape. */
export function updateProfile(input: UpdateProfileInput): Promise<CurrentUser> {
  return authClient.patch<CurrentUser>(ENDPOINTS.auth.me, input);
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

/**
 * Self-service password change. The backend revokes EVERY session on success
 * (DEC-022), so the caller must treat the session as ended and re-authenticate.
 */
export function changePassword(input: ChangePasswordInput): Promise<void> {
  return authClient.post<void>(ENDPOINTS.auth.changePassword, input);
}

export function logout(): Promise<void> {
  return authClient.post<void>(ENDPOINTS.auth.logout);
}
